// Answers/Responses management section - handles CRUD operations for responses
import React, { useState, useMemo } from "react";
import { useToast } from "../providers/ToastProvider";
import { useEditing } from "../lib/hooks/useEditing";
import { useFilter } from "../lib/hooks/useFilter";
import { useDataSource } from "../lib/hooks/useDataSource";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import EditorModal from "../components/ui/EditorModal";
import Confirm from "../components/ui/Confirm";
import { Input, Label } from "../components/ui/Input";
import { Textarea } from "../components/ui/Textarea";
import SearchableSelect from "../components/ui/SearchableSelect";
import TruncatedText from "../components/ui/TruncatedText";
import ResponsiveTable from "../components/ui/ResponsiveTable";
import { useRestAuth } from "../providers/RestAuthProvider";
import { useLocalization } from "../providers/LocalizationProvider";
import UserSelect from "../components/ui/UserSelect";
import { USER_ROLES } from "../lib/config/constants";
import { logger } from "../lib/utils/logger";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/Select";

export default function AnswersSection({ dataSource, showTrash }) {
  // Get hooks for notifications, authentication, and localization
  const { notify } = useToast();
  const { user } = useRestAuth();
  const { t, language, userLanguageDetermined } = useLocalization();

  // Use data source hook with current language for response operations
  const {
    data: responses,
    isLoading,
    error,
    create,
    update,
    softDelete,
    restore,
    hardDelete,
    refresh,
    clearError
  } = useDataSource(dataSource, 'responses', language, userLanguageDetermined);

  const filter = useFilter("");
  const { editing, setEditing, openNew, openEdit, close } = useEditing({
    questionId: "",
    language: 'fr',
    answer: '',
    userId: user?.id
  });

  // Local state for question selection
  const [selectedQuestionId, setSelectedQuestionId] = useState("");
  const [confirm, setConfirm] = useState({ open: false });

  // Ref to preserve userId across language changes
  const preservedUserIdRef = React.useRef(editing?.userId || user?.id);

  // Update ref when editing changes
  React.useEffect(() => {
    if (editing?.userId) {
      preservedUserIdRef.current = editing.userId;
    } else if (!editing && user?.id) {
      preservedUserIdRef.current = user.id;
    }
  }, [editing?.userId, user?.id]);

  // Reset selectedQuestionId when editing changes
  React.useEffect(() => {
    if (editing?.questionId) {
      setSelectedQuestionId(editing.questionId);
    } else if (!editing) {
      setSelectedQuestionId("");
    }
  }, [editing]);

  // Ensure responses are loaded on component mount when language is determined
  React.useEffect(() => {
    if (responses.length === 0 && !isLoading && userLanguageDetermined) {
      refresh(true);
    }
  }, [responses.length, isLoading, userLanguageDetermined, refresh]);

  // Refresh responses and questions when language changes
  // Note: App.jsx already calls updateLanguage and reload(), but we need to ensure
  // questions are reloaded for this section since they're needed for display
  React.useEffect(() => {
    if (userLanguageDetermined && dataSource && dataSource.reload) {
      // Preserve editing state before language change
      const currentEditing = editing;
      const preservedUserId = currentEditing?.userId || preservedUserIdRef.current || user?.id;

      // Reload questions with the new language (responses will be reloaded by refresh)
      // We reload questions separately because they're language-specific and needed for display
      dataSource.reload(['questions'], language).then(() => {
        // Restore userId in editing state after questions are reloaded
        if (currentEditing && preservedUserId) {
          setEditing({ ...currentEditing, userId: preservedUserId });
        }
      }).catch(err => {
        logger.error('Failed to reload questions on language change', err);
        // Still try to restore editing state even if reload fails
        if (currentEditing && preservedUserId) {
          setEditing({ ...currentEditing, userId: preservedUserId });
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, userLanguageDetermined]);
  const data = useMemo(() =>
    responses.filter(r => {
      const isVisible = showTrash ? r.deletedAt : !r.deletedAt;
      if (!isVisible) return false;

      // Use the pre-transformed answer text from data source
      return r.answer.toLowerCase().includes(filter.value.toLowerCase());
    }),
    [responses, showTrash, filter.value]
  );

  const onSave = async (e) => {
    e.preventDefault();
    clearError();

    const f = new FormData(e.currentTarget);
    const userId = f.get('userId') + "";
    const contentLanguageCode = f.get('contentLanguage') + "";

    try {
      if (editing?.id) {
        // PATCH only updates translation text, userId is optional
        const updatePayload = {
          answerText: f.get('answer') + ""
        };
        if (userId) {
          updatePayload.userId = userId;
        }
        await update(editing.id, updatePayload);
        notify({ type: 'success', message: t('responses.responseUpdated') });
      } else {
        // CREATE needs questionId
        const questionId = f.get('questionId');
        const createPayload = {
          questionId: questionId || selectedQuestionId || dataSource.state.questions.find(q => !q.deletedAt)?.id || "",
          answerText: f.get('answer') + "",
          contentLanguageCode
        };
        await create(createPayload);
        notify({ type: 'success', message: t('responses.responseCreated') });
      }
      setEditing(null);
      await refresh(true); // Force refresh to get latest data
    } catch (error) {
      // Error is already handled by the hook
      logger.error('Save error', error);
    }
  };

  const askDelete = (r) => {
    setConfirm({
      open: true,
      title: t('responses.deleteConfirm'),
      description: t('responses.deleteDescription'),
      onConfirm: async () => {
        try {
          await softDelete(r.id);
          notify({ type: 'success', message: t('responses.responseMovedToTrash') });
          setConfirm({ open: false });
          await refresh(true);
        } catch (error) {
          logger.error('Delete error', error);
        }
      },
      onCancel: () => setConfirm({ open: false })
    });
  };

  const isAdmin = user?.role === USER_ROLES.ADMIN;

  // Note: Questions and users data should be loaded by their respective sections
  // or by the parent component. This section only manages responses data.

  // Memoize user names to ensure they update when users are loaded
  const userNames = React.useMemo(() => {
    const names = {};
    if (isAdmin && dataSource.state.users && dataSource.state.users.length > 0) {
      dataSource.state.users.forEach(user => {
        const firstName = user.firstName || '';
        const lastName = user.lastName || user.name || '';
        names[user.id] = [firstName, lastName].filter(Boolean).join(' ') || "—";
      });
    }
    return names;
  }, [isAdmin, dataSource.state.users]);

  const userName = (uid) => {
    if (!uid) return t('common.notAssigned');
    if (!isAdmin) return t('common.notAssigned');

    // If users are not loaded yet, show loading
    if (!dataSource.state.users || dataSource.state.users.length === 0) {
      return t('common.loadingText');
    }

    // If users are loaded but this specific user is not found, show unknown
    return userNames[uid] || t('common.unknown');
  };

  const canManage = (ownerId) => isAdmin || ownerId === user?.id;

  // Render loading state
  if (isLoading && responses.length === 0) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{t('responses.title')}</CardTitle>
            <div className="flex items-center gap-2">
              {filter.input}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-900"></div>
              <span className="text-slate-500">{t('responses.loadingResponses')}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Render error state
  if (error && responses.length === 0) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{t('responses.title')}</CardTitle>
            <div className="flex items-center gap-2">
              {filter.input}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            <div className="text-red-600 text-center">
              <p className="font-medium">{t('responses.loadingError')}</p>
              <p className="text-sm">{error}</p>
            </div>
            <Button
              onClick={() => refresh(true)}
            >
              {t('responses.retry')}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="space-y-4">
          <CardTitle>{t('responses.title')}</CardTitle>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-end">
            {!showTrash && (
              <Button onClick={openNew} className="w-full sm:w-auto">
                {t('responses.newResponse')}
              </Button>
            )}
            <div className="w-full sm:w-auto">
              {filter.input}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Error banner */}
        {error && (
          <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="text-destructive text-sm">{error}</span>
              <Button
                onClick={clearError}
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive"
              >
                ×
              </Button>
            </div>
          </div>
        )}

        <ResponsiveTable
          data={data}
          columns={[
            { header: t('responses.response'), className: "max-w-xs" },
            { header: t('responses.question'), className: "max-w-xs" },
            { header: t('responses.role') },
            ...(isAdmin ? [{ header: t('common.linkedUser') }] : []),
            { header: t('common.actions'), className: "w-56 text-right" }
          ]}
          renderRow={(r) => (
            <tr key={r.id} className="border-b hover:bg-muted/50">
              <td className="py-2 max-w-xs">
                <TruncatedText text={r.answer} maxLength={60} />
              </td>
              <td className="py-2 max-w-xs">
                <TruncatedText
                  text={r.questionId ?
                    (dataSource.state.questions.find(q => q.id === r.questionId)?.enonce || t('questions.questionNotAssigned'))
                    : t('questions.questionNotAssigned')
                  }
                  maxLength={40}
                />
              </td>
              <td className="py-2">{r.userRole}</td>
              {isAdmin && <td className="py-2">{userName(r.userId)}</td>}
              <td className="py-2 text-right space-x-2">
                {!showTrash ? (
                  <>
                    {canManage(r.userId) && (
                      <Button
                        onClick={() => openEdit(r)}
                        variant="outline"
                        size="sm"
                        disabled={isLoading}
                      >
                        {t('common.edit')}
                      </Button>
                    )}
                    {canManage(r.userId) && (
                      <Button
                        onClick={() => askDelete(r)}
                        variant="outline"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        disabled={isLoading}
                      >
                        {t('common.delete')}
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    {canManage(r.userId) && (
                      <Button
                        onClick={async () => { await restore(r.id); notify({ type: 'success', message: t('responses.responseRestored') }); await refresh(); }}
                        variant="outline"
                        size="sm"
                        disabled={isLoading}
                      >
                        {t('common.restore')}
                      </Button>
                    )}
                    {canManage(r.userId) && (
                      <Button
                        onClick={async () => { await hardDelete(r.id); notify({ type: 'success', message: t('responses.responseDeletedPermanent') }); await refresh(); }}
                        variant="outline"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        disabled={isLoading}
                      >
                        {t('common.deletePermanent')}
                      </Button>
                    )}
                  </>
                )}
              </td>
            </tr>
          )}
          renderMobileCard={(r) => (
            <div className="space-y-3 w-full max-w-full overflow-hidden">
              <div className="w-full max-w-full overflow-hidden">
                <div className="text-sm font-medium text-muted-foreground mb-1">{t('responses.response')}</div>
                <div className="text-sm break-words overflow-wrap-anywhere">{r.answer}</div>
              </div>
              <div className="w-full max-w-full overflow-hidden">
                <div className="text-sm font-medium text-muted-foreground mb-1">{t('responses.question')}</div>
                <div className="text-sm break-words overflow-wrap-anywhere">
                  {r.questionId ?
                    (dataSource.state.questions.find(q => q.id === r.questionId)?.enonce || t('questions.questionNotAssigned'))
                    : t('questions.questionNotAssigned')
                  }
                </div>
              </div>
              <div className="flex justify-between items-center w-full max-w-full">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-muted-foreground mb-1">{t('responses.role')}</div>
                  <div className="text-sm break-words">{r.userRole}</div>
                </div>
                {isAdmin && (
                  <div className="min-w-0 flex-1 ml-2">
                    <div className="text-sm font-medium text-muted-foreground mb-1">{t('common.linkedUser')}</div>
                    <div className="text-sm break-words">{userName(r.userId)}</div>
                  </div>
                )}
              </div>
              {canManage(r.userId) && (
                <div className="flex gap-2 pt-2 w-full max-w-full">
                  {!showTrash ? (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEdit(r)}
                        className="flex-1 min-w-0"
                        disabled={isLoading}
                      >
                        {t('common.edit')}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => askDelete(r)}
                        className="flex-1 min-w-0 text-destructive hover:text-destructive"
                        disabled={isLoading}
                      >
                        {t('common.delete')}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={async () => { await restore(r.id); notify({ type: 'success', message: t('responses.responseRestored') }); await refresh(); }}
                        className="flex-1 min-w-0"
                        disabled={isLoading}
                      >
                        {t('common.restore')}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={async () => { await hardDelete(r.id); notify({ type: 'success', message: t('responses.responseDeletedPermanent') }); await refresh(); }}
                        className="flex-1 min-w-0 text-destructive hover:text-destructive"
                        disabled={isLoading}
                      >
                        {t('common.deletePermanent')}
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
          emptyMessage={t('common.noItems')}
        />
      </CardContent>

      <EditorModal open={!!editing} title={editing?.id ? t('responses.editResponse') : t('responses.newResponse')} onClose={close} onSubmit={onSave}>
        {isAdmin ? (
          <UserSelect name="userId" label={t('responses.linkedUser')} users={dataSource.state.users || []} defaultValue={editing?.userId || preservedUserIdRef.current || user?.id} />
        ) : (
          <>
            <div className="space-y-2">
              <Label htmlFor="linkedUser">{t('responses.linkedUser')}</Label>
              <Input id="linkedUser" value={user?.name || ""} readOnly disabled />
            </div>
            <input type="hidden" name="userId" value={user?.id || ""} />
          </>
        )}
        <div className="w-full min-w-0 max-w-full">
          <SearchableSelect
            label={t('responses.question')}
            name="questionId"
            value={editing?.questionId || selectedQuestionId}
            defaultValue={editing?.questionId || dataSource.state.questions.find(q => !q.deletedAt)?.id || ""}
            placeholder={t('common.searchQuestion')}
            options={dataSource.state.questions?.filter(q => !q.deletedAt) || []}
            getOptionValue={(option) => option.id}
            getOptionLabel={(option) => option.enonce}
            getOptionSearchText={(option) => option.enonce?.toLowerCase() || ""}
            required
            loading={!dataSource.state.questions || dataSource.state.questions.length === 0}
            emptyMessage={t('common.noQuestionsFound')}
            className="max-w-full"
            onValueChange={(value) => {
              setSelectedQuestionId(value);
            }}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="answer">{t('responses.response')}</Label>
          <Textarea
            id="answer"
            name="answer"
            defaultValue={editing?.answer || ''}
            required
            rows={4}
            className="min-h-[100px]"
            placeholder={t('common.enterAnswer')}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="contentLanguage">{t('common.contentLanguage')}</Label>
          <Select name="contentLanguage" disabled={editing?.id} defaultValue={language || 'fr'}>
            <SelectTrigger>
              <SelectValue placeholder={t('users.selectLanguage')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="fr">Français</SelectItem>
              <SelectItem value="en">English</SelectItem>
              <SelectItem value="es">Español</SelectItem>
              <SelectItem value="pt">Português</SelectItem>
            </SelectContent>
          </Select>
        </div>

      </EditorModal>
      <Confirm {...confirm} />
    </Card>
  );
}
